import {IMediaData, IMediaViewData} from "./IMediaData";
import {ITextTrackData} from "./TextTrackTypes";
import IMediaView from "./IMediaView";
import InputValidator from "../../util/validation/InputValidator";
import {IMediaUpdateReq} from "./IUpdateMediaReq";
import {HttpLinkTemplate, Link} from "../link/LinkTypes";
import {FileStatusType} from "./FileStatusType";
import {MediaFileType, PosterThumbFiles} from "./MediaFileTypes";
import {MediaType} from "./MediaType";
import {ListType} from "./list/ListType";
import { WidthHeight } from "../types/WidthHeight";

enum Codec {
    Unknown = "unknown",
    AAC = "aac",
    H264 = "h264",
    H264_AAC = "h264_aac",
    H265_AAC = "h265_aac",
    MP3 = "mp3"
}

class CodecUtil {
    static isAudio(codec: Codec): boolean {
        switch (codec) {
            case Codec.AAC:
                return true;
            case Codec.H264:
                return false;
            case Codec.H264_AAC:
                return false;
            case Codec.H265_AAC:
                return false;
            case Codec.MP3:
                return true;
            case Codec.Unknown:
                return false;
            default:
                throw new Error(`Codec not implemented: ${codec}`);
        }
    }
}

enum Format {
    Unknown = "unknown",
    MP4 = "mp4",
    M4A = "m4a",
    MP3 = "mp3",
    JPG = "jpg",
    JPEG = "jpeg",
    PNG = "png",
    HEIC = "heic",
    WebP = "webp",
    SMIL = "smil"
}

class FormatUtil {
    static isImage(format: Format): boolean {
        switch (format) {
            case Format.JPG:
            case Format.JPEG:
            case Format.PNG:
            case Format.HEIC:
                return true;
            default:
                return false;
        }
    }

    static extToFormat(ext: string): Format {
        switch (ext) {
            case Format.MP4:
                return Format.MP4;
            case Format.M4A:
                return Format.M4A;
            case Format.MP3:
                return Format.MP3;
            case Format.JPG:
                return Format.JPG;
            case Format.JPEG:
                return Format.JPEG;
            case Format.PNG:
                return Format.PNG;
            case Format.WebP:
                return Format.WebP;
            case Format.HEIC:
                return Format.HEIC;
            case Format.SMIL:
                return Format.SMIL;
            default:
                return Format.Unknown;
        }
    }
}

enum MediaProtocol {
    Dash = "dash",
    Hls = "hls",
    Http = "http",
    Icecast = "icecast"
}

interface IClipCreateReq {
    start: number | null;
    end: number | null;
    updateMediaReq: IMediaUpdateReq;
    transcodeVodId: number | null;
}

interface IMergeCreateReq {
    mediaIds: number[];
    updateMediaReq: IMediaUpdateReq;
    transcodeVodId: number | null;
}

interface IMediaFileCreateReq {
    ingestMethodType: string;
    downloadUrl: string | null;
    filename: string;
    jobType: string;
    mediaFileType: string;
}

interface IMediaFileData {
    fileId: number;
    mediaId: number;
    fileStatusType: FileStatusType;
    mediaFileType: MediaFileType;
    bytes: number;
    format: string;
    groupFileId: number | null;
    codec: Codec | null;
    fps: number | null;
    width: number | null;
    height: number | null;
    created: number;
    updated: number;
}

class MediaFile {
    data: IMediaFileData;

    constructor(data: IMediaFileData) {
        this.data = data;
    }

    getWidthHeight(): WidthHeight | null {
        if (this.data.width && this.data.height) {
            return {width: this.data.width, height: this.data.height};
        } else {
            return null;
        }
    }
}

interface IMediaViewVideoAudioData extends IMediaViewData {
    cuepoints: any[];
    files: IMediaFileData[];
    texttracks: ITextTrackData[];
}

interface IMediaVideoAudioView extends IMediaView {
    data: IMediaViewVideoAudioData;
    iv: InputValidator;
    createMediaUpdateReq: (refIdChanged: boolean) => IMediaUpdateReq;
    createMediaClipReq: (start: number | null, end: number | null, transcodeVodId: number) => IClipCreateReq;
    version: () => string;
    clone: () => IMediaView
    validate: (id: string, value: any) => void;
}

class MediaVideoAudioView {
    protected getMediaPoster(files: IMediaFileData[]): PosterThumbFiles {
        const poster = files.find(f => f.mediaFileType == MediaFileType.Poster && f.fileStatusType == FileStatusType.Ready);
        const thumb = files.find(f => f.mediaFileType == MediaFileType.Thumb && f.fileStatusType == FileStatusType.Ready);
        return {
            poster: poster || null,
            thumb: thumb || null
        }
    }

    static getThumb(files: IMediaFileData[]): IMediaFileData | null {
        return files.find(f => f.mediaFileType == MediaFileType.Thumb && f.fileStatusType == FileStatusType.Ready) || null;
    }

}


enum MediaSourceType {
    UploadedSource = "uploaded_source"
}

interface ITenses {
    past: string;
    pastProgressive: string;
    present: string;
}

enum MediaStateType {
    Active = "active",
    Inactive = "inactive"
}

class MediaStateUtil {

    static getTenses(mediaState: MediaStateType): ITenses {
        return mediaState == MediaStateType.Active
            ? {past: 'activated', pastProgressive: 'activating', present: 'activate'}
            : {past: 'deactivated', pastProgressive: 'deactivating', present: 'deactivate'};
    }
}

enum MediaStatusType {
    AwaitingIngest = "awaiting_ingest",
    Ingesting = "ingesting",
    Processing = "processing",
    Ready = "ready",
    Deleted = "deleted",
    Error = "error"
}

interface ICustomMediaValiadtionError {
    fieldId: string;
    mediaId: number;
    bcMediaId: string;
    value: any;
    msg: string;
}

interface IRefIdConflictResult {
    listId: number | null,
    mediaId: number | null,
    mediaType: MediaType,
    refId: string;
    msg: string;
}

class MediaStatusUtil {


    static getLabel(type: MediaStatusType): string {
        switch (type) {
            case MediaStatusType.AwaitingIngest:
                return "Waiting";
            case MediaStatusType.Ingesting:
                return "Uploading";
            case MediaStatusType.Processing:
                return "Processing";
            case MediaStatusType.Ready:
                return "Ready";
            case MediaStatusType.Deleted:
                return "Deleted";
            case MediaStatusType.Error:
                return "Error";
            default:
                throw new Error(`Unsupported MediaStatusType: ${type}`);
        }
    }

    static hasFinished(status: MediaStatusType): boolean {
        return status == MediaStatusType.Ready || status == MediaStatusType.Error || status == MediaStatusType.Deleted;
    }
}

export {
    Codec,
    CodecUtil,
    Format,
    FormatUtil,
    IClipCreateReq,
    IMergeCreateReq,
    ICustomMediaValiadtionError,
    IMediaFileData,
    IMediaViewVideoAudioData,
    IMediaVideoAudioView,
    MediaVideoAudioView,
    MediaFile,
    MediaProtocol,
    MediaStateType,
    MediaStateUtil,
    MediaSourceType,
    MediaStatusType,
    MediaStatusUtil,
    IRefIdConflictResult
}